package fyi.moko.hotpl.ui.map

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.size
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.drawscope.translate
import androidx.compose.ui.graphics.lerp
import androidx.compose.ui.unit.dp

/**
 * 지도 핀 한 개를 이 DrawScope 전체 크기에 맞춰 그린다. 캔버스 비율은 [PIN_ASPECT]여야 한다.
 *
 * Android는 Compose Canvas 안에서, iOS는 ImageBitmap에 그려 UIImage로 넘긴다 — 두 플랫폼이
 * **같은 픽셀**을 그리는 유일한 방법이라 여기 commonMain에 둔다. 웹(Leaflet divIcon)만 SVG를
 * 따로 든다.
 *
 * ── 입체 핀 (시안 C, 2026-09-14) ──────────────────────────────
 * 머리를 구체로 본다: 좌상단 반사광, 가장자리로 갈수록 어두워지는 방사 그라데이션, 끝점 아래
 * 접지 그림자. 색은 [color] 하나에서 밝기만 파생하므로 색 채널의 결정(감정 색을 남길지, 한
 * 색으로 닫을지)과 이 모양은 독립이다.
 *
 * ── 속 빈 핀 = 안 가본 곳 (R-15) ──────────────────────────────
 * 채움을 뒤집는다: 상아색 구체에 색 링과 머리의 색 고리만. 같은 실루엣·같은 크기라 "내 장소"로는
 * 똑같이 읽히고, 비어 있음이 곧 "아직"이다.
 */
fun DrawScope.drawPlacePin(color: Color, hollow: Boolean) {
    val scale = size.height / PIN_CANVAS_HEIGHT
    val pin = pinPath(scale)
    val headCenter = Offset(size.width / 2f, scale * PIN_HEAD_CENTER_Y)
    val tip = Offset(size.width / 2f, scale * PIN_PATH_HEIGHT)

    // ── 입체 핀 (시안 C, 2026-09-14) ──────────────────────────────
    // 머리를 구체로 본다: 좌상단 반사광, 가장자리로 갈수록 어두워지는 방사 그라데이션,
    // 끝점 아래 접지 그림자. 색은 [color] 하나에서 밝기만 파생하므로 색 채널의
    // 결정(감정 색을 남길지, 한 색으로 닫을지)과 이 모양은 독립이다.
    val deep = lerp(color, Color.Black, 0.45f)

    // 접지 그림자: 끝점에 깔린 타원. 캔버스 아래쪽 여백(PIN_CANVAS_HEIGHT - PIN_PATH_HEIGHT)이 이 자리다.
    drawOval(
        brush = Brush.radialGradient(
            0f to Color.Black.copy(alpha = 0.34f),
            1f to Color.Black.copy(alpha = 0f),
            center = tip,
            radius = scale * 3.8f,
        ),
        topLeft = Offset(tip.x - scale * 3.8f, tip.y - scale * 1.3f),
        size = Size(scale * 7.6f, scale * 2.6f),
    )
    // 떨어진 그림자: 같은 실루엣을 아래로 내려 두 겹. 블러 대신 겹으로 부드럽게.
    translate(left = scale * 0.5f, top = scale * 1.6f) {
        drawPath(pin, Color.Black.copy(alpha = 0.12f), style = Stroke(width = scale * 1.6f))
        drawPath(pin, Color.Black.copy(alpha = 0.22f))
    }

    val glossCenter = Offset(headCenter.x - scale * 1.5f, headCenter.y - scale * 2.0f)
    val glossRadius = scale * 9.5f
    if (hollow) {
        // 흰 구체에 색 링만: "아직 비어 있다"가 명암으로 먼저 읽힌다.
        //
        // 처음 안(2026-09-14)은 상아색→회갈색 방사 그라데이션 몸통 + 그라데이션 링이었는데, 실기기에서
        // "그래픽이 깨진 느낌"이 났다(2026-09-17). 이유: 몸통 아래쪽이 탁한 회갈색으로 떨어지고,
        // 링이 위는 밝고 아래는 어두워 두께가 고르지 않게 보였다. 그래서 몸통은 거의 흰색으로만
        // 아주 옅게, 링은 **단색**으로, 안쪽 고리는 가늘게 — 채운 핀과 같은 흰 외곽선으로 지도와 뗀다.
        drawPath(
            pin,
            Brush.radialGradient(
                0f to Color.White,
                0.7f to Color.White,
                1f to Color(0xFFEDE9E2),
                center = glossCenter,
                radius = glossRadius,
            ),
        )
        drawPath(pin, Color.White.copy(alpha = 0.9f), style = Stroke(width = scale * 2.2f))
        drawPath(pin, color, style = Stroke(width = scale * 1.3f))
        drawCircle(color, radius = scale * 2.3f, center = headCenter, style = Stroke(width = scale * 0.9f))
    } else {
        drawPath(
            pin,
            Brush.radialGradient(
                0f to lerp(color, Color.White, 0.5f),
                0.35f to lerp(color, Color.White, 0.08f),
                0.8f to lerp(color, Color.Black, 0.2f),
                1f to deep,
                center = glossCenter,
                radius = glossRadius,
            ),
        )
        // 흰 외곽선은 지도의 흰 도로 위에서도 윤곽을 지킨다.
        drawPath(pin, Color.White.copy(alpha = 0.9f), style = Stroke(width = scale * 0.65f))
        // 구멍: 어두운 원을 살짝 아래 겹쳐 안으로 들어간 느낌.
        drawCircle(deep.copy(alpha = 0.35f), radius = scale * 2.5f, center = headCenter + Offset(0f, scale * 0.3f))
        drawCircle(Color.White, radius = scale * 2.3f, center = headCenter)
    }
    // 반사광: 좌상단 타원, 위에서 아래로 사라진다. 구체라는 단서는 이 하나로 충분하다.
    val hx = scale * 2.9f
    val hy = scale * 2.0f
    val hTop = headCenter.y - scale * 4.9f
    drawOval(
        brush = Brush.verticalGradient(
            0f to Color.White.copy(alpha = if (hollow) 0.5f else 0.8f),
            1f to Color.White.copy(alpha = 0f),
            startY = hTop,
            endY = hTop + hy * 2f,
        ),
        topLeft = Offset(headCenter.x - scale * 1.5f - hx, hTop),
        size = Size(hx * 2f, hy * 2f),
    )
}

/**
 * The map pin as a small glyph, e.g. the Journal's Been / Want to go chips — the chip shows
 * the exact pin it filters for, so the legend lives where the choice is made.
 */
@Composable
fun PlacePinIcon(hollow: Boolean, modifier: Modifier = Modifier, color: Color = NEUTRAL_PIN_COLOR) {
    Canvas(modifier.size(height = 18.dp, width = 18.dp * PIN_ASPECT)) {
        drawPlacePin(color = color, hollow = hollow)
    }
}

/** Material "place" glyph, translated so the head starts at the top-left of the canvas. */
private fun pinPath(scale: Float): Path = Path().apply {
    // Original glyph: head centre (12, 9) r = 7, tip at (12, 22), in a 24-unit box.
    // Shifted by (-5, -2) so the shape spans x 0..14, y 0..20 exactly.
    moveTo(7f * scale, 0f)
    cubicTo(3.13f * scale, 0f, 0f, 3.13f * scale, 0f, 7f * scale)
    cubicTo(0f, 12.25f * scale, 7f * scale, 20f * scale, 7f * scale, 20f * scale)
    cubicTo(7f * scale, 20f * scale, 14f * scale, 12.25f * scale, 14f * scale, 7f * scale)
    cubicTo(14f * scale, 3.13f * scale, 10.87f * scale, 0f, 7f * scale, 0f)
    close()
}

/** 핀 실루엣의 높이(단위). 끝점은 캔버스 위에서 이만큼 아래에 있다. */
const val PIN_PATH_HEIGHT = 20f
/**
 * 끝점 아래 접지 그림자 자리로 2단위를 남긴다. Android 마커 앵커는 캔버스 아래 가운데라
 * 좌표는 끝점보다 그만큼(40dp 핀에서 약 3.6dp) 아래에 찍힌다 — 지도 축척에서 눈에 안 띈다.
 * iOS는 `centerOffset`으로 끝점을 정확히 좌표에 맞춘다.
 */
const val PIN_CANVAS_HEIGHT = 22f
private const val PIN_HEAD_CENTER_Y = 7f
const val PIN_ASPECT = 14f / PIN_CANVAS_HEIGHT
